import React, { useState, useEffect } from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Chip,
    CircularProgress,
    Typography,
    Select,
    MenuItem,
    FormControl,
    Box,
    TextField,
    InputAdornment
} from '@mui/material';
import {
    Delete as DeleteIcon,
    Search as SearchIcon,
} from '@mui/icons-material';
import memberService from '../services/memberService';
import { toast } from 'react-toastify';

const MemberRow = ({ member, hostId, onRefresh }) => {
    const [loading, setLoading] = useState(false);
    const [role, setRole] = useState(member.role);

    // Sync state if props change (e.g. after refresh)
    useEffect(() => {
        setRole(member.role);
    }, [member.role]);

    const handleRemove = async () => {
        if (member.role === 'account-owner') return;

        if (window.confirm(`Bạn có chắc muốn xóa thành viên ${member.email}?`)) {
            setLoading(true);
            try {
                await memberService.removeMember(hostId, member.email);
                toast.success('Đã xóa thành viên thành công');
                onRefresh();
            } catch (error) {
                toast.error('Xóa thành viên thất bại');
            } finally {
                setLoading(false);
            }
        }
    };

    const handleRoleChange = async (event) => {
        const newRole = event.target.value;
        // Don't update if same value
        if (newRole === role) return;

        setRole(newRole); // Optimistic update
        setLoading(true);
        try {
            await memberService.updateMemberRole(hostId, member.email, newRole);
            toast.success('Cập nhật quyền thành công');
            onRefresh();
        } catch (error) {
            toast.error('Cập nhật quyền thất bại');
            setRole(member.role); // Revert on error
        } finally {
            setLoading(false);
        }
    };

    const isOwner = member.role === 'account-owner';

    return (
        <TableRow hover>
            <TableCell sx={{ color: 'text.primary', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>{member.email}</TableCell>
            <TableCell sx={{ color: 'text.secondary', fontSize: '0.7rem', fontFamily: 'monospace' }}>
                {member.openaiUserId || '-'}
            </TableCell>
            <TableCell>
                {isOwner ? (
                    <Chip label="Account Owner" color="secondary" size="small" sx={{ fontSize: '0.65rem' }} />
                ) : (
                    <FormControl size="small" variant="standard" sx={{ minWidth: { xs: 90, sm: 120 } }}>
                        <Select
                            value={role}
                            onChange={handleRoleChange}
                            disableUnderline
                            disabled={loading}
                            sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                        >
                            <MenuItem value="standard-user">Standard User</MenuItem>
                            <MenuItem value="account-admin">Account Admin</MenuItem>
                        </Select>
                    </FormControl>
                )}
            </TableCell>
            <TableCell sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                {member.addedAt ? new Date(member.addedAt).toLocaleDateString('vi-VN') : '-'}
            </TableCell>
            <TableCell align="right">
                {loading ? (
                    <CircularProgress size={20} />
                ) : (
                    !isOwner && (
                        <IconButton onClick={handleRemove} color="error" size="small">
                            <DeleteIcon />
                        </IconButton>
                    )
                )}
            </TableCell>
        </TableRow>
    );
};

const MemberTable = ({ members, hostId, onRefresh }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredMembers, setFilteredMembers] = useState([]);

    useEffect(() => {
        let result = members || [];

        if (searchTerm) {
            result = result.filter(m =>
                (m.email || '').toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        setFilteredMembers(result);
    }, [members, searchTerm]);

    return (
        <Box>
            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                <TextField
                    placeholder="Tìm kiếm thành viên..."
                    size="small"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                            </InputAdornment>
                        ),
                    }}
                    sx={{ flexGrow: 1 }}
                />
            </Box>

            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', overflowX: 'auto' }}>
                <Table sx={{ minWidth: 650 }}>
                    <TableHead sx={{ bgcolor: 'action.hover' }}>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>Email</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>OpenAI User ID</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Quyền hạn</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Ngày tham gia</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>Hành động</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredMembers.length > 0 ? (
                            filteredMembers.map((member) => (
                                <MemberRow
                                    key={member.id}
                                    member={member}
                                    hostId={hostId}
                                    onRefresh={onRefresh}
                                />
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                                    <Typography color="text.secondary">
                                        Không tìm thấy thành viên phù hợp.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default MemberTable;
